/* eslint-disable max-len */
import React from 'react';
import { compose } from 'recompose';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { H1, H3, H4 } from 'components/core/Typography';
import { BackToTop } from 'components/core/BackToTop';
import { Page } from 'components/core/Page';
import { Helmet } from 'react-helmet';
import { Link } from 'components/core/Link';
import './legalPage.scss';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

const enhance = compose(
	withPageViewTracking(PAGE_TYPES.LEGAL)
);

export function LegalConsumerPrivacyPage() {
	return (
		<Page className="legal-page">
			 <Helmet>
                <link rel="canonical" href="https://www.sparefoot.com/legal/privacy.html" />
            </Helmet>
			<div className="section-content">
				<H1 size="large">
					PRIVACY POLICY
				</H1>
				<p>
					Last updated and effective date: July 27, 2023
				</p>
				<p>
					SpareFoot, Inc. is a wholly-owned indirect subsidiary of Storable, Inc.
				</p>
				<p>
					This Privacy Policy describes how Storable, Inc. and its affiliated companies (collectively, “ Storable”) collects, uses, and discloses information about you, including Personal information. This Privacy Policy also tells you about the rights and choices you have with respect to your Personal Information.
				</p>
				<p>
					Storable, Inc., is located at 10900 Research Blvd., Ste. 160C PMB 3099, Austin, TX 78759, and may be contacted at <Link href="mailto:privacy@storable.com">privacy@storable.com</Link>.
				</p>

				<H4 size="small">
					What Our Privacy Policy Covers
				</H4>
				<p>
					This Privacy Policy covers Storable’s customer-facing operations and applies to the Personal Information Storable obtains in various contexts, including when you:
				</p>
				<ul>
					<li>Access or use our website at <Link href="www.storable.com">www.storable.com</Link> or any other websites, mobile websites, and web-based applications that Storable operates and that link to this policy (collectively, “Website”).</li>
					<li>Access or use our products and services.</li>
					<li>Communicate with us via phone, email, text, and other forms of electronic messages, including engaging with us on social media.</li>
					<li>Interact with media integrations on our Website and other third-party websites like Facebook, YouTube, LinkedIn, and Twitter.</li>
					<li>Interact with the third-party partners we work with to offer, provide, and improve our products and services to our customers.</li>
					<li>Interact with us offline, including by attending any of our programs or events.</li>
				</ul>
				<p>
					This Privacy Policy applies to the Personal Information collected through our Website, regardless of the country where you are located.
				</p>
				<p>
					This Privacy Policy also explains how we collect, use, and disclose Personal Information we may collect in our role as a service provider or processor for our business customers. When acting in this capacity, generally, our customer’s privacy policy will apply. We will handle information, including Personal Information, that we receive, access, use, or maintain in this capacity based on the customer’s direction. We encourage you to read the privacy policies of those businesses and exercise any Personal Information rights by contacting them directly.
				</p>
				<p>
					Our Website may include links to websites, plug-ins, services, social networks, and applications operated and maintained by third parties. Clicking on those links or enabling those connections may allow the third party to collect or share information about you. Please note that we have no control over the privacy practices of third parties, and we encourage you to review the privacy practices of every website you visit. As such, this Policy also applies to information that Storable obtains through third parties, including information that you provide to or that is collected by any third party. However, this Policy does not apply to the collection and processing of that information by the third party. Those third parties may have their own privacy policies. We encourage you to read the policies of those businesses before providing information to or through them, and to contact them directly if you wish to exercise any Personal Information rights related to that data.
				</p>
				<p>
					We may provide different or additional privacy notices in connection with certain activities, products, services, or platforms that may not be included in this Privacy Policy to supplement or clarify our privacy practices or provide you with additional choices regarding your Personal Information with respect to those activities.
				</p>
				<p>
					Please read this Privacy Policy carefully to understand our policies and practices for collecting, using, and disclosing your Personal Information. By engaging with our Website, you accept and consent to the practices described in this Privacy Policy. This Privacy Policy may change from time to time (see Changes  to Our Privacy Policy). Your continued engagement with our Website after any such revisions indicates that you accept and consent to them, so please check the Privacy Policy periodically for updates.
				</p>

				<H4 size="small">Information We May Collect and Disclose About You</H4>
				<p>
					The information Storable collects about you and how we collect, use, and disclose it depends on the nature of the relationship you have with us, your interactions with us and our products and services, the services and products you use, the choices you make, and the requirements of applicable law.
				</p>
				<p>
					We have collected the following categories of Personal Information from consumers within the last 12 months to support business operations, including for the business purposes listed in the chart below. The chart also lists, for each category, the categories of recipients to which we disclose the information. The below chart also notes how we collect and disclose Sensitive Personal Information.
				</p>
				<table>
					<tr>
						<td valign="top" colSpan="1" rowSpan="1">
							<H4 size="small">Categories of Personal Information</H4>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<H4 size="small">Categories of Sources of Personal Information</H4>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<H4 size="small">Business Purpose for Collecting the Personal Information</H4>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<H4 size="small">Categories of Recipients to which the Personal Information is Disclosed</H4>
						</td>
					</tr>
					<tr>
						<td valign="top" colSpan="1" rowSpan="1">
							<p><b>Identifiers</b> such as</p>
							<ul>
								<li>
									real name
								</li>
								<li>
									email address
								</li>
								<li>
									home address
								</li>
								<li>
									online identifier (username)
								</li>
								<li>
									unique personal identifier (cookies, beacons, pixel tags)
								</li>
								<li>
									IP (or proxy server) address
								</li>
							</ul>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Consumers</p>
							<p>Customers</p>
							<p>Social Media Platforms </p>
							<p>Publicly Available Sources</p>
							<p>Corporate Affiliates</p>
							<p>Data Analytics Providers</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>
								Fulfill the purpose for which the information was provided to us.
							</p>
							<p>
								Notify you about changes to our Website, products, or
								services.
							</p>
							<p>
								Create, maintain, customize, and secure your account with
								us
							</p>
							<p>
								Provide you with support and to respond to your inquiries,
								including to investigate and address your concerns and monitor and
								improve our responses.
							</p>
							<p>
								Carry out our obligations and enforce our rights arising from any
								contracts entered into between you and us, including for billing
								and collection.
							</p>
							<p>
								Maintain the security and operation of our Website, and for our
								internal analytics and reporting purposes.
							</p>
							<p>
								Market our products and services.
							</p>
							<p>
								Provide, support, personalize, and develop our Website, products,
								and services.
							</p>
							<p>
								Help maintain the safety, security, and integrity of our Website,
								products and services, databases and other technology assets, and
								business.
							</p>
							<p>
								Comply with our legal or regulatory obligations, to establish or
								exercise our rights, and to defend against a legal claim.
							</p>
							<p>
								For testing, research, analysis, and product development,
								including to develop and improve our Website, products, services,
								marketing, or customer relationships and experiences.
							</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Corporate Affiliates</p>
							<p>Business Communication and Collaboration Providers</p>
							<p>Customer Relationship Managers</p>
							<p>Payment Processors</p>
							<p>Finance and Accounting Tools</p>
							<p>Hosting Service Providers</p>
							<p>Social Media Platforms </p>
							<p>Sales and Marketing Tools</p>
							<p>Data Analytics Providers</p>
						</td>
					</tr>
					<tr>
						<td valign="top" colSpan="1" rowSpan="1">
							<p><b>Personal Information Categories Listed in the California Customer Records statute. Cal. Civ. Code &sect; 1798.80(e)</b> such as</p>
							<ul>
								<li>
									Driver&rsquo;s License Number
								</li>
								<li>
									telephone number
								</li>
								<li>
									credit card number
								</li>
								<li>
									debit card number
								</li>
								<li>
									bank account number with routing number
								</li>
							</ul>
							<p>
								Some Personal Information included in this category may overlap
								with other categories.
							</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Consumers</p>
							<p>Customers</p>
							<p>Corporate Affiliates</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>
								Fulfill the purpose for which the information was provided to us.
							</p>
							<p>
								Provide you with support and to respond to your inquiries,
								including to investigate and address your concerns and monitor and
								improve our responses.
							</p>
							<p>
								Carry out our obligations and enforce our rights arising from any
								contracts entered into between you and us, including for billing
								and collection.
							</p>
							<p>
								Comply with our legal or regulatory obligations, to establish or
								exercise our rights, and to defend against a legal claim.
							</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Corporate Affiliates</p>
							<p>
								Business Communication and Collaboration Providers
							</p>
							<p>
								Customer Relationship Managers
							</p>
							<p>Payment Processors</p>
							<p>Finance and Accounting Tools</p>
							<p>Hosting Service Providers</p>
						</td>
					</tr>
					<tr>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>
								<b>Internet Activity Information</b> such as
							</p>
							<ul>
								<li>browser type </li>
								<li>browser settings </li>
								<li>date/time stamps associated with your usage of the Website</li>
								<li>pages and files of the Website viewed </li>
								<li>searches on the Website</li>
								<li>which Website features you use</li>
								<li>Internet service provider </li>
								<li>mobile carrier</li>
							</ul>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Consumers</p>
							<p>Corporate Affiliates</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>
								Ensure that we present our Website content in the most effective
								manner for you and for your computer.
							</p>
							<p>
								Create, maintain, customize, and secure your account with
								us.
							</p>
							<p>
								Provide you with support and to respond to your inquiries,
								including to investigate and address your concerns and monitor and
								improve our responses.
							</p>
							<p>
								Maintain the security and operation of our Website, and for our
								internal analytics and reporting purposes.
							</p>
							<p>
								Market our products and services.
							</p>
							<p>
								Provide, support, personalize, and develop our Website, products,
								and services.
							</p>
							<p>
								Help maintain the safety, security, and integrity of our Website,
								products and services, databases and other technology assets, and
								business.
							</p>
							<p>
								Comply with our legal or regulatory obligations, to establish or
								exercise our rights, and to defend against a legal claim.
							</p>
							<p>
								For testing, research, analysis, and product development,
								including to develop and improve our Website, products, services,
								marketing, or customer relationships and experiences.
							</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Corporate Affiliates</p>
							<p>Social Media Platforms</p>
							<p>Sales and Marketing Tools</p>
							<p>Data Analytics Providers</p>
						</td>
					</tr>
					<tr>
						<td valign="top" colSpan="1" rowSpan="1">
							<p><b>Protected Classification Characteristics Under California or Federal Law</b> such as</p>
							<ul>
								<li>gender expression</li>
								<li>age (in the form of date of birth)</li>
							</ul>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Consumers</p>
							<p>Customers</p>
							<p>Corporate Affiliates</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Fulfill the purpose for which the information was provided to us.</p>
							<p>
								Provide you with support and to respond to your inquiries, including to
								investigate and address your concerns and monitor and improve our responses.
							</p>
							<p>
								Carry out our obligations and enforce our rights arising from any contracts
								entered into between you and us, including for billing and collection.
							</p>
							<p>
								Comply with our legal or regulatory obligations, to establish or exercise
								our rights, and to defend against a legal claim.
							</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Corporate Affiliates</p>
							<p>Hosting Service Providers</p>
						</td>
					</tr>
					<tr>
						<td valign="top" colSpan="1" rowSpan="1">
							<p><b>Geolocation Data</b> such as</p>
							<ul>
								<li>information about your device's location</li>
							</ul>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Consumers</p>
							<p>Corporate Affiliates</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Create, maintain, customize, and secure your account with us.</p>
							<p>Provide you with support and to respond to your inquiries, including to investigate and address your concerns and monitor and improve our responses.</p>
							<p>Maintain the security and operation of our Website, and for our internal analytics and reporting purposes.</p>
							<p>Market our products and services.</p>
							<p>Provide, support, personalize, and develop our Website, products, and services.</p>
							<p>Help maintain the safety, security, and integrity of our Website, products and services, databases and other technology assets, and business.</p>
							<p>Comply with our legal or regulatory obligations, to establish or exercise our rights, and to defend against a legal claim.</p>
							<p>For testing, research, analysis, and product development, including to develop and improve our Website, products, services, marketing, or customer relationships and experiences.</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Corporate Affiliates</p>
						</td>
					</tr>
				</table>

				<p>
					We have collected the following categories of Sensitive Personal Information from consumers within the last 12 months to support business operations, including for the business purposes listed in the chart below. The chart also lists, for each category, the categories of recipients to which we disclose the information
				</p>
				<table>
					<tr>
						<td valign="top" colSpan="1" rowSpan="1">
							<H4 size="small">Categories of Sensitive Personal Information</H4>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<H4 size="small">Categories of Sources of Sensitive Personal Information</H4>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<H4 size="small">Business Purpose for Collecting the Sensitive Personal Information</H4>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<H4 size="small">Categories of Recipients to which the Sensitive Personal Information is Disclosed</H4>
						</td>
					</tr>
					<tr>
						<td valign="top" colSpan="1" rowSpan="1">
							<p><b>Identifiers</b> such as</p>
							<ul>
								<li>Social Security Number</li>
								<li>Driver&rsquo;s License Number</li>
							</ul>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Consumers</p>
							<p>Customers</p>
							<p>Corporate Affiliates</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Fulfill the purpose for which the information was provided to us.</p>
							<p>Provide you with support and to respond to your inquiries, including to investigate and address your concerns and monitor and improve our responses.</p>
							<p>Carry out our obligations and enforce our rights arising from any contracts entered into between you and us, including for billing and collection.</p>
							<p>Comply with our legal or regulatory obligations, to establish or exercise our rights, and to defend against a legal claim.</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Corporate Affiliates</p>
							<p>Hosting Service Providers</p>
						</td>
					</tr>
					<tr>
						<td valign="top" colSpan="1" rowSpan="1">
							<p><b>Personal Information Categories Listed in the California Customer Records statute. Cal. Civ. Code &sect; 1798.80(e)</b> such as</p>
							<ul>
								<li>Driver&rsquo;s License Number</li>
								<li>credit card number</li>
								<li>debit card number</li>
								<li>bank account number with routing number</li>
							</ul>
							<p>Some Sensitive Information included in this category may overlap with other categories.</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Consumers</p>
							<p>Customers</p>
							<p>Corporate Affiliates</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Fulfill the purpose for which the information was provided to us.</p>
							<p>Provide you with support and to respond to your inquiries, including to investigate and address your concerns and monitor and improve our responses.</p>
							<p>Carry out our obligations and enforce our rights arising from any contracts entered into between you and us, including for billing and collection.</p>
							<p>Comply with our legal or regulatory obligations, to establish or exercise our rights, and to defend against a legal claim.</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Corporate Affiliates</p>
							<p>Business Communication and Collaboration Providers</p>
							<p>Customer Relationship Managers</p>
							<p>Payment Processors</p>
							<p>Finance and Accounting Tools</p>
							<p>Hosting Service Providers</p>
						</td>
					</tr>
					<tr>
						<td valign="top" colSpan="1" rowSpan="1">
							<p><b>Geolocation Data</b> such as</p>
							<ul>
								<li>
									<span>precise information about your device's location</span>
								</li>
							</ul>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Consumers</p>
							<p>Corporate Affiliates</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Create, maintain, customize, and secure your account with us.</p>
							<p>Provide you with support and to respond to your inquiries, including to investigate and address your concerns and monitor and improve our responses.</p>
							<p>Maintain the security and operation of our Website, and for our internal analytics and reporting purposes.</p>
							<p>Market our products and services.</p>
							<p>Provide, support, personalize, and develop our Website, products, and services.</p>
							<p>Help maintain the safety, security, and integrity of our Website, products and services, databases and other technology assets, and business.</p>
							<p>Comply with our legal or regulatory obligations, to establish or exercise our rights, and to defend against a legal claim.</p>
							<p>For testing, research, analysis, and product development, including to develop and improve our Website, products, services, marketing, or customer relationships and experiences.</p>
						</td>
						<td valign="top" colSpan="1" rowSpan="1">
							<p>Corporate Affiliates</p>
						</td>
					</tr>
				</table>
				<p>
					If we combine or connect non-personal information with Personal Information so that it directly or indirectly identifies an individual or so that we may draw inferences about an individual, we treat the combined information as Personal Information and, when applicable, Sensitive Personal Information.
				</p>
				<p>
					Employees or job applicants may provide additional information through job applications, benefits enrollment, payroll, or direct deposit enrollment, or other parts of the employment process. We maintain a separate privacy notice for employees and job applicants.
				</p>

				<H4 size="small">Legal Basis for Processing</H4>
				<p>
					Under the General Data Protection Regulation ("GDPR"), we are only allowed to process your Personal Information, where we have a legal basis to do so. For those of the processing activities described above, which are subject to the GDPR, we rely in particular on the following legal bases:
				</p>
				<ul>
					<li>
						Consent: For some processing activities, we require your consent before we engage in the processing of your Personal Information for a specific purpose (Art. 6 sec. 1 lit. a GDPR);
					</li>
					<li>
						Performance of a contract: We process your Personal Information, where the processing is necessary for the performance of a contract to which you are a party or in order to take steps at your request prior to entering into the contract (Art. 6 sec. 1 lit. b GDPR);
					</li>
					<li>
						Compliance with a legal obligation: We process your Personal Information, where the processing is necessary for complying with a legal obligation (Art. 6 sec. 1 lit. c GDPR);
					</li>
					<li>
						Legitimate interest: We process your Personal Information, where the processing is necessary for the legitimate interest we pursue, except where such interests are overridden by your fundamental rights and freedoms (Art. 6 sec. 1 lit. f GDPR). The legitimate interests we pursue are further specified in the tables above, namely in the column "Business Purpose for Collecting the Personal Information".
					</li>
				</ul>

				<H4 size="small">Data Retention</H4>
				<p>
					Except as otherwise permitted or required by applicable law or regulation, we will only retain your Personal Information for as long as necessary to fulfill the purposes for which we collected it, as required to satisfy any legal, accounting, or reporting obligations, to exercise our legal rights and defend claims, or as necessary to resolve disputes. To determine the appropriate retention period for Personal Information, we consider applicable legal requirements, the amount, nature, and sensitivity of the Personal Information, the potential risk of harm from unauthorized use or disclosure of your Personal Information, the purposes for which we process your Personal Information, and whether we can achieve those purposes through other means.
				</p>

				<H4 size="small">Selling or Sharing Your Personal Information</H4>
				<p>
					Storable does not sell your information, including Personal Information and Sensitive Personal Information.
				</p>
				<p>
					Consistent with your consent, Storable shares unique personal identifiers that are Personal Information with third-party data analytics providers for behavioral tracking, also referred to as cross-context behavioral advertising or targeted advertising, through technology within web-browsers like cookies, beacons, and pixel tags. For information regarding the rights you may have to opt-out of information sharing for the purposes of cross-context behavioral advertising, see the "Exercising Your Rights" section below.
				</p>

				<H4 size="small">How We Collect Information About You</H4>
				<p>
					We collect information, including Personal Information, from you through the categories of sources listed above. We use different methods to collect information from and about you. Those methods include the following:
				</p>
				<p>
					Personal Information you voluntarily provide to us when you register for a service or product, express an interest in obtaining information about us, our products and services, or our third-party partners, or when you contact us or a third-party partner about our products or services.
				</p>
				<p>
					We also collect certain information automatically when you visit, use, or navigate our Website or products and services. When you visit our Website, we or third parties we work with automatically collect certain information using technologies such as cookies and other data collection tools. For more information, please see the "Cookies and Similar Tracking Technologies" section below.
				</p>
				<p>
					We may also obtain information from third parties that we partner, collaborate, or work with.
				</p>
				<p>
					You also may provide information for us to publish or display on public platforms or transmit to other users of our products and services or third parties (collectively, "User Contributions"). For example, we maintain official pages on third-party social media websites such as Facebook, Instagram, LinkedIn, Twitter, and YouTube. When you interact with us on social media websites, we collect analytics regarding your likes, interests, and feedback regarding our products and services. When you interact with platforms like Facebook, Instagram, LinkedIn, Twitter, and YouTube, you are subject to their terms of use and privacy policies. We may collect certain information about you that you have posted and made public on these third-party social media websites. We strongly advise that you avoid posting/sharing confidential, financial, or other sensitive information like your Social Security number, bank account number, phone number, mailing address, or email address when posting to a social media site. Any posts that you make on our official social media pages (including posts that contain pictures, comments, suggestions, opinions, complaints, or personal information) are publicly available to others who use those pages, as well as the companies operating the websites on which they appear. In order to better understand your rights and obligations with regard to such posts, please refer to the privacy policies posted on those social media sites you are visiting.
				</p>
				<p>
					You may submit other forms of User Contributions for posting and transmission to others at your own risk. Although we limit access to certain pages and/or you may set certain privacy settings for User Contributions by logging into your account profile, please be aware that no security measures are perfect or impenetrable. Additionally, we cannot control the actions of any Website users with whom you choose to share your User Contributions. Therefore, we do not guarantee that your User Contributions will only be viewed by authorized persons.
				</p>
				<p>
					We may obtain information about you from other sources, such as data analytics providers, marketing or advertising service providers, fraud prevention service providers, vendors that provide services on our behalf, or publicly available sources.
				</p>

				<H4 size="small">Cookies and Similar Tracking Technologies</H4>
				<p>
					We and our third-party partners use cookies on our Website. A cookie is
					a small file placed on the hard drive of your computer or device. You
					may configure cookie settings through the prompts displayed when
					visiting our Website. You may also have the opportunity to further
					configure cookie settings through your internet browser. However, if you
					select to refuse certain cookies or reject certain cookie settings, you
					may be unable to access certain parts of our Website. Upon confirmation
					of your preferences, our system will issue cookies when you direct your
					browser to our Website, or limit their application as appropriate.
				</p>

				<H4 size="small">Web Beacons and Pixels</H4>
				<p>
					Our Website pages and emails may contain web beacons (small transparent embedded images or objects, also known as clear gifs, pixel tags, and single-pixel gifs) that permit us, for example, to count Website page visitors or email readers, or to compile other similar statistics such as recording Website content popularity or verifying system and server integrity.
				</p>
				<p>
					We use cookies or other automatic information collection technologies consistent with your consent to help us maintain the security of our Website and your account, prevent crashes, fix bugs, save your preferences, and assist with basic site functions. These allow us to distinguish you from other Website users to deliver a better and more personalized service when you browse our Website. It also allows us to improve our Website by enabling us to:
				</p>
				<ul>
					<li>Estimate our audience size and usage patterns.</li>
					<li>Store your preferences so we may customize our Website according to your individual interests.</li>
					<li>Speed up your searches.</li>
					<li>Recognize you when you return to our Website.</li>
				</ul>
				<p>
					As also described above, we also may use these technologies to, consistent with your consent, collect information about your online activities over time and across third-party websites or other online services (behavioral tracking). Some content or applications, including advertisements, on the Website or Storable platforms are served by third parties, including advertisers, ad networks and servers, content providers, and application providers. We may permit third parties to use tracking technologies on our Website for analytics and advertising, including to help manage and display advertisements and to tailor advertisements to your interests. These include but may not be limited to Google Analytics and Meta Pixels. The third parties use their technology to provide advertising about products and services tailored to your interests which may appear either on our Website or on other websites. We do not control how these third-party tracking technologies operate or how they may use the collected information.
				</p>

				<H4 size="small"><i>Do Not Track Signals</i></H4>
				<p>
					Our website is not designed to respond to "do not track" signals received from browsers.
				</p>
				<p>
					For information on how you can provide consent or opt out of behavioral tracking on our Website, and learn about how we respond to web browser signals and other mechanisms that enable you to opt into or opt out of behavioral tracking, click here <Link href="https://privacyrequest.storable.com/">https://privacyrequest.storable.com/</Link>. Some features of our Website or platforms may not be available to you as a result of your choice to opt-out. Please email <Link href="mailto:privacy@storable.com">privacy@storable.com</Link> if you have questions about this.
				</p>

				<H4 size="small">The Purposes for Collecting and Using Your Personal Information</H4>
				<p>
					We may collect and use Personal Information for the various purposes listed in the charts above. We may also use your information for any other purpose with your consent.
				</p>
				<p>
					We will not use your information for any materially different, unrelated, or incompatible purposes without providing you notice.
				</p>

				<H4 size="small">Disclosure of Your Personal Information</H4>
				<p>
					In addition to the specific situations discussed elsewhere in this Privacy Policy, we may disclose your information, including Personal Information:
				</p>
				<ul>
					<li>
						With any member of our corporate group, which means our subsidiaries, our ultimate holding company and its subsidiaries, and our affiliates.
					</li>
					<li>
						With your consent.
					</li>
					<li>
						For any other purposes that we disclose when you provide the information.
					</li>
				</ul>
				<p>
					We may also disclose your Personal Information in order to do any of the following:
				</p>
				<ul>
					<li>
						Fulfill the purpose for which you provide it.
					</li>
					<li>
						Comply with any court order, law, subpoena, or other legal process, including to respond to any government or regulatory request.
					</li>
					<li>
						Enforce or apply any agreements between Storable and you, if applicable.
					</li>
					<li>
						Protect and defend our rights and property, our Website, the users of our Website, and/or our affiliated parties.
					</li>
					<li>
						Protect the safety of Storable, users of our Website, our customers, or others.
					</li>
					<li>
						Complete a sale, transfer, or merger of some or all of our assets or equity, or reorganization, dissolution, or similar event involving our business (including in contemplation of such transactions). Your information may be among the shared or transferred business assets. If such transfer is subject to any mandatory restrictions under applicable laws, we will comply with those restrictions.
					</li>
					<li>
						Satisfy any other purpose disclosed by us when you provide the information.
					</li>
				</ul>
				<p>
					We may also provide aggregated information (consisting of combined
					information of a number of individuals but that does not identify those
					individuals) to third parties, who may use such information to analyze
					patterns or statistics regarding use of our products and services.
					&nbsp; &nbsp;
				</p>

				<H3 size="small">How We Protect Your Personal Information</H3>
				<p>
					Personal Information is maintained on our servers or those of our service providers, and access is restricted to individuals with a specific need to access the information to provide products and services to you, or otherwise for the purposes described in this Privacy Policy.
				</p>
				<p>
					We realize individuals trust us to protect their Personal Information. We educate our employees about the importance of confidentiality and privacy. We require all third parties with a business need to access Personal Information we have collected to adhere to stringent privacy policies and agree to maintain the information in confidence and safeguard it against unauthorized access or disclosure.
				</p>
				<p>
					We maintain commercially reasonable safeguards to help protect Personal Information we have collected against accidental loss or unauthorized use, disclosure, alteration, or destruction. We periodically review our data security practices and consider appropriate new technology methods to ensure compliance with federal and state data privacy and information security regulations. We subject our systems to simulated intrusion tests, however, no security system or method of transmitting or storing electronic data is ever completely secure or impenetrable. In the event that your Personal Information is accessed or disclosed in an unauthorized manner, we will notify you as required by applicable law.
				</p>

				<H3 size="small">Your Privacy Rights</H3>
				<p>
					Consistent with applicable law, you may exercise the rights described in this section. Please note that some of the rights may vary depending on the jurisdiction in which you reside.
				</p>
				<p>
					Under certain consumer data privacy laws of some U.S. states, such as the California Consumer Privacy Act ("CCPA"), the Virginia Consumer Data Protection Act ("VCDPA"), the Colorado Privacy Act ("CPA"), and the Connecticut Data Privacy Act ("CTDPA") (collectively "U.S. State Privacy Law"), California, Colorado, Connecticut, and Virginia residents have specific rights regarding their Personal Information, also referred to as personal data. Likewise, under the General Data Protection Regulation (EU) 2016/679 ("EU-GDPR") or the UK General Data Protection Regulation ("UK-GDPR"; collectively, "GDPR"), data subjects who either (i) are in the EU or the UK or (ii) are affected by processing activities in the context of an establishment of Storable in the EU or the UK have specific rights regarding their Personal Information, which the GDPR also calls personal data.
				</p>
				<p>
					If applicable, this section describes your U.S. State Privacy Law rights as well as your GDPR rights and explains how to exercise those rights. The section will not apply, however, if Storable does not collect any Personal Information about you or if all of the information we collect does not fall within the protection of applicable law. This Section only includes information Storable has collected as a business. It does not address information Storable may handle as a service provider or processor for other businesses.
				</p>

				<H3 size="small">Exercising Your Rights</H3>
				<p>
					To exercise any of the rights described in this Section, please submit a request by either:
				</p>
				<ul>
					<li>
						Emailing us at <Link href="mailto:privacy@storable.com">privacy@storable.com</Link>.
					</li>
					<li>
						Submitting this data request form: <Link href="https://privacyrequest.storable.com/">https://privacyrequest.storable.com/</Link>
					</li>
				</ul>
				<p>
					Only you, or sometimes someone legally authorized to act on your behalf, may submit a request to exercise your Personal Information rights. Your request must:
				</p>
				<ul>
					<li>
						Describe your request with sufficient detail that allows us to properly understand, evaluate, and respond to it.
					</li>
					<li>
						Provide sufficient information, which will likely need to match information we previously collected from you, that allows us to reasonably verify you are the person about whom we collected Personal Information or an authorized representative ("Authorized Agent"). In some instances, we may ask you to provide a signed declaration under penalty of perjury that you are the consumer whose Personal Information is the subject of the request. For requests seeking particularly sensitive information, we may require additional proof of identification.
					</li>
				</ul>
				<p>
					If you would like, you may designate an Authorized Agent to make a request on your behalf. We will deny requests from agents that do not submit proof of authorization from you. If you designate an Authorized Agent to make a request on your behalf, to verify that an authorized agent has authority to act for you, we may require:
				</p>
				<ul>
					<li>
						A copy of a power of attorney or require that you provide the authorized agent with written permission.
					</li>
					<li>
						You to verify your own identity with us directly.
					</li>
				</ul>
				<p>
					We will only use Personal Information provided in the request to verify the requestor's identity or authority to make it, except for record-keeping purposes. We will delete this information as soon as practicable after complying with the request.
				</p>
				<p>
					If you submit a request to Storable and Storable is the service provider or processor of your Personal Information, we will do our best to route the request to the proper party so long as we have sufficient information to do so on your behalf.
				</p>

				<H3 size="small"><i>Right to Know and Data Portability/Right of Access</i></H3>
				<p>
					You may have the right to request that we disclose certain information to you about our collection and use of your Personal Information (the "Right to Know"). You may also have the right to confirm whether Storable is processing your Personal Information and to access this Personal Information (the "Right to Access" and "Right to Data Portability"). Once we receive your request and confirm your identity, we will disclose to you:
				</p>
				<ul>
					<li>
						The categories of Personal Information we collected about you.
					</li>
					<li>
						The categories of sources for the Personal Information we collected about you.
					</li>
					<li>
						Our business or commercial purpose for collecting, selling, or sharing that Personal Information.
					</li>
					<li>
						The categories of recipients to whom we disclose Personal Information. The specific pieces of Personal Information we collected about you in a portable and readily usable format when Storable processes the information through automated or searchable means (also called a "data portability" request).
					</li>
					<li>
						In relation to data subjects protected by the GDPR, any further personal data that we are obliged to provide under Art. 15 GDPR.
					</li>
				</ul>

				<H3 size="small"><i>Right to Delete</i></H3>
				<p>
					You may have the right to request that we delete any of your Personal Information that you have provided or that we have collected from you and retained, subject to certain exceptions (the "Right to Delete"). Once we receive your request and confirm your identity, we will review your request to see if an exception requiring us to retain the information applies. We may deny a deletion request if retaining the information is necessary for us or our service provider(s) to:
				</p>
				<ol>
					<li>
						Complete the transaction for which we collected the Personal Information, provide a good or service that you requested or otherwise take actions reasonably anticipated within the context of our ongoing business relationship with you, fulfill the terms of a written warranty or product recall conducted in accordance with federal law, or otherwise perform a contract with you.
					</li>
					<li>
						Help to ensure security and integrity to the extent the use of the consumer's Personal Information is reasonably necessary and proportionate for those purposes.
					</li>
					<li>
						Debug to identify and repair errors that impair existing intended functionality.
					</li>
					<li>
						Exercise free speech, ensure the right of another consumer to exercise the consumer's free speech rights, or exercise another right provided for by law.
					</li>
					<li>
						Comply with the California Electronic Communications Privacy Act pursuant to Chapter 3.6 (commencing with Section 1546) of Title 12 of Part 2 of the Penal Code.
					</li>
					<li>
						Engage in public or peer-reviewed scientific, historical, or statistical research in the public interest that conforms or adheres to all other applicable ethics and privacy laws, when the information's deletion may likely render impossible or seriously impair the ability to complete such research, if you previously provided informed consent.
					</li>
					<li>
						Enable solely internal uses that are reasonably aligned with consumer expectations based on your relationship with us and compatible with the context in which the consumer provided the information.
					</li>
					<li>
						Comply with a legal obligation.
					</li>
				</ol>

				<H3 size="small"><i>Right to Correct Inaccurate Information</i></H3>
				<p>
					If we maintain inaccurate information about you, you may have the right
					to request correction of that inaccurate Personal Information, taking
					into account the nature of the Personal Information and the purposes of
					the Personal Information processing. We will use commercially reasonable
					efforts to correct this information.
				</p>

				<H3 size="small"><i>Right to Limit</i></H3>
				<p>
					Storable&rsquo;s use of Sensitive Personal Information falls into one
					of limited business purposes. &nbsp;We do not share Sensitive Personal
					Information for any other purpose.
				</p>

				<H3 size="small"><i>Right to Restriction of Processing</i></H3>
				<p>
					Data subjects protected by the GDPR under certain circumstances have
					the right to obtain restriction of processing; e.g. where (i) the
					accuracy of the personal data is contested, for as long as the accuracy
					is verified; (ii) the processing is unlawful and you oppose the erasure
					of the personal data; (iii) we no longer need the personal, but they are
					required by you for the establishment, exercise or defense of a legal
					claim; or (iv) you have objected to the processing pending the
					assessment of your objection.
				</p>

				<H3 size="small"><i>Right to Withdraw your Consent</i></H3>
				<p>
					Where we process your personal data on the basis of your consent, you
					can withdraw your consent at any time. This will not affect the
					lawfulness of our processing activities based on your consent that
					happened before your withdrawal.
				</p>

				<H3 size="small"><i>Right to Opt-In or Opt-Out of Sale / Right to Object</i></H3>
				<p>
					U.S. State Privacy Law gives consumers the right to request to opt out of the processing of Personal Information for purposes of (i) targeted advertising, (ii) the sale or sharing of Personal Information, and/or (iii) profiling in furtherance of decisions that produce legal or similarly significant effects concerning the consumer. Likewise, under the GDPR, data subjects have the right to object (i) on grounds relating to his or her particular situation, certain processing activities, or (ii) processing activities for direct marketing purposes, which includes profiling to the extent that it is related to such direct marketing.
				</p>
				<p>
					If you are age 16 or older, you have the right to direct us to not sell your Personal Information at any time (the "right to opt-out"). We do not sell the Personal Information of consumers we actually know are less than 16 years old, unless we receive affirmative authorization (the "right to opt-in") from either the consumer who is between 13 and 15 years old, or the parent or guardian of a consumer less than 13 years old. Consumers who opt-in to Personal Information sales may opt-out of future sales at any time.
				</p>
				<p>
					Once you make an opt-out request, we will wait at least 12 months before asking you to reauthorize Personal Information sales. However, you may change your mind and opt back in to Personal Information sales at any time by submitting a request online through this link.
				</p>
				<p>
					You do not need to create an account with us to exercise your opt-out rights. We will only use Personal Information provided in an opt-out request to review and comply with the request.
				</p>

				<H3 size="small"><i>Non-Discrimination</i></H3>
				<p>
					We will not discriminate against you for exercising any of your rights. Unless permitted by U.S. State Privacy Law, we will not:
				</p>
				<ul>
					<li>Deny you goods or services.</li>
					<li>Charge you different prices or rates for goods or services, including through granting discounts or other benefits, or imposing penalties.</li>
					<li>Provide you a different level or quality of goods or services.</li>
					<li>Suggest that you may receive a different price or rate for goods or services or a different level or quality of goods or services.</li>
				</ul>
				<p>
					However, we may offer you certain financial incentives permitted by U.S. State Privacy Law that can result in different prices, rates, or quality levels, such as offering coupons, discounts, promotions, other price reductions, or promotional offers for products and services in exchange for providing your Personal Information. Any permitted financial incentive we offer will reasonably relate to your Personal Information's value and contain written terms that describe the program's material aspects. Participation in a financial incentive program requires your prior opt-in consent, which you may revoke at any time. Do not provide your Personal Information if you do not want to opt in to these offers. After you receive a promotional offer, you may opt-out by contacting us.
				</p>

				<H3 size="small"><i>Response Timing and Format</i></H3>
				<p>
					We will confirm receipt of your request within 10 business days. If you do not receive confirmation within the 10-day timeframe, please contact <Link href="mailto:privacy@storable.com">privacy@storable.com</Link>.
				</p>
				<p>
					We endeavor to substantively respond to a verifiable consumer request within 30 days of its receipt. If we require more time (up to another 30 days), we will inform you of the reason and extension period in writing. In relation to a request under the GDPR, we will inform you of the action taken without undue delay and in any event within one month of receipt of your request.
				</p>
				<p>
					We will deliver our written response by mail or electronically, at your option.
				</p>
				<p>
					If we cannot comply with your rights request, our response will explain the reasons we cannot comply, if applicable. For data portability requests, we will select a format to provide your Personal Information that is readily useable and should allow you to transmit the information from one entity to another entity without hindrance.
				</p>
				<p>
					We do not charge a fee to process or respond to your verifiable consumer request unless it is excessive, repetitive, or manifestly unfounded. If we determine that the request warrants a fee, we will tell you why we made that decision and provide you with a cost estimate before completing your request.
				</p>

				<H3 size="small"><i>Appeal Process</i></H3>
				<p>
					Residents of Colorado, Connecticut, and Virginia may submit an appeal of a decision to deny a rights request within a reasonable time of receiving the denial. If you are a resident of one of these states and would like to submit an appeal, please email <Link href="mailto:privacy@storable.com">privacy@storable.com</Link>.
				</p>
				<p>
					<i>Data subjects protected under the GDPR also have the right to lodge a complaint with the supervisory authority.</i>
				</p>

				<H3 size="small">Children's Online Privacy</H3>
				<p>
					We do not direct our Website to minors, and we do not knowingly collect Personal Information from children under 16 or as defined by local legal requirements. If we learn we have mistakenly or unintentionally collected or received Personal Information from a child without appropriate consent, we will delete it. If you believe we mistakenly or unintentionally collected any information from or about a child, please contact us at <Link href="mailto:privacy@storable.com">privacy@storable.com</Link>.
				</p>

				<H3 size="small">Consent to Personal Information Transfer</H3>
				<p>
					We are based in the United States. We may process, store, and transfer the Personal Information we collect in and to a country outside your own with different privacy laws that may or may not be as comprehensive as your own.
				</p>
				<p>
					If you are located outside the United States and choose to provide information to us, please note that we transfer information, including Personal Information, to the United States. By submitting your Personal Information or engaging with our Website, you consent to this transfer, storing, or processing. &nbsp;Where our processing of Personal Information in the US involves a transfer from either the EU or the UK, we ensure appropriate safeguards; e.g. by concluding agreements incorporating the EU Commission's standard contractual clauses for international transfers and / or as the case maybe the UK equivalent, where necessary with additional measures in order to ensure a level of data protection essentially equivalent to that in the EU / UK.
				</p>
				<p>
					We will take all steps reasonably necessary to ensure that your information is treated securely and in accordance with this Privacy Policy and no transfer of your Personal Information will take place to an organization or a country unless there are adequate controls in place including the security of your Personal Information.
				</p>

				<H3 size="small">For More Information</H3>
				<p>Contact Storable at:</p>
				<ul>
					<li>
						<Link href="mailto:privacy@storable.com">privacy@storable.com</Link>
					</li>
				</ul>

				<H3 size="small">Changes To This Policy</H3>
				<p>
					We may revise this Privacy Policy from time to time and will post the
					date it was last updated at the top of this Privacy Policy. We will
					provide additional notice to you if we make any changes that materially
					affect your privacy rights.
				</p>
				<BackToTop />
			</div>
		</Page>
	);
}

export default enhance(LegalConsumerPrivacyPage);
